// src/pages/Contact.js - Updated with backend integration
import React, { useState, useEffect } from 'react';
import './Contact.css';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faEnvelope, 
  faMapMarkerAlt, 
  faCheckCircle, 
  faExclamationCircle,
  faClock,
  faMobile,
  faBuilding,
  faPaperPlane,
  faUser,
  faComment
} from '@fortawesome/free-solid-svg-icons';

// API URL - should match your backend setup
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/contact';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState(''); // 'success' or 'error'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [animated, setAnimated] = useState(false);

  // Animation on scroll
  useEffect(() => {
    const handleScroll = () => {
      const element = document.querySelector('.contact-container');
      if (!element) return;
      
      const position = element.getBoundingClientRect();
      if (position.top < window.innerHeight * 0.75 && !animated) {
        setAnimated(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on initial load
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [animated]);

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    // Validate name
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      errors.email = 'Valid email is required';
      isValid = false;
    }

    // Validate phone (optional, but validate format if provided)
    if (formData.phone.trim() && !/^[0-9\s\+\-\(\)]{10,15}$/.test(formData.phone)) {
      errors.phone = 'Valid phone number is required';
      isValid = false;
    }

    // Validate message
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
      isValid = false;
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message is too short';
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear field error when user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    setFormData({ 
      ...formData, 
      [name]: value 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setStatus('Sending your message...');
    setStatusType('');

    try {
      // Call the backend API
      const response = await fetch(`${API_URL}/contact-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        // Success
        setStatus('Your message has been sent! We will contact you shortly.');
        setStatusType('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        
        // Automatically clear success message after 5 seconds
        setTimeout(() => {
          setStatus('');
          setStatusType('');
        }, 5000);
      } else {
        throw new Error(data.msg || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('Error sending message. Please try again or call us directly.');
      setStatusType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Contact Page Header */}
      <div className="contact-header">
        <div className="container">
          <div className={`page-title-wrapper ${animated ? 'animated' : ''}`}>
            <h1 className="page-title">Contact Us</h1>
            <div className="title-separator"></div>
            <p className="page-subtitle">
              Get in touch with our team for a free consultation and quote
            </p>
          </div>
        </div>
      </div>
      
      <div className="contact-container container">
        <div className="contact-wrapper">
          {/* Contact Form */}
          <div className={`contact-form-container ${animated ? 'animated' : ''}`}>
            <div className="form-header">
              <div className="form-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <h2>Send Us a Message</h2>
              <p>Fill out the form below and we'll get back to you as soon as possible</p>
            </div>
            
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className={`form-group ${fieldErrors.name ? 'error' : ''}`}>
                <label htmlFor="name">
                  <FontAwesomeIcon icon={faUser} className="field-icon" />
                  Full Name
                </label>
                <input 
                  id="name"
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {fieldErrors.name && <div className="error-message">{fieldErrors.name}</div>}
              </div>

              <div className={`form-group ${fieldErrors.email ? 'error' : ''}`}>
                <label htmlFor="email">
                  <FontAwesomeIcon icon={faEnvelope} className="field-icon" />
                  Email Address
                </label>
                <input 
                  id="email"
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {fieldErrors.email && <div className="error-message">{fieldErrors.email}</div>}
              </div>

              <div className={`form-group ${fieldErrors.phone ? 'error' : ''}`}>
                <label htmlFor="phone">
                  <FontAwesomeIcon icon={faPhone} className="field-icon" />
                  Phone Number
                </label>
                <input 
                  id="phone"
                  type="tel"
                  name="phone"
                  placeholder="Your Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {fieldErrors.phone && <div className="error-message">{fieldErrors.phone}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="service">
                  <FontAwesomeIcon icon={faBuilding} className="field-icon" />
                  Service Required
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="" disabled>Select a Service</option>
                  <option value="New Roof Installation">New Roof Installation</option>
                  <option value="Roof Repairs">Roof Repairs</option>
                  <option value="Roof Maintenance">Roof Maintenance</option>
                  <option value="Guttering & Fascias">Guttering & Fascias</option>
                  <option value="Chimney Repairs">Chimney Repairs</option>
                  <option value="Emergency Services">Emergency Services</option>
                  <option value="Roof Inspection">Roof Inspection</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className={`form-group ${fieldErrors.message ? 'error' : ''}`}>
                <label htmlFor="message">
                  <FontAwesomeIcon icon={faComment} className="field-icon" />
                  Your Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  placeholder="Please describe your project or inquiry"
                  value={formData.message}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                {fieldErrors.message && <div className="error-message">{fieldErrors.message}</div>}
              </div>

              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} className="button-icon" />
                    Send Message
                  </>
                )}
              </button>
              
              {status && (
                <div className={`form-status ${statusType}`}>
                  <FontAwesomeIcon 
                    icon={statusType === 'success' ? faCheckCircle : faExclamationCircle} 
                    className="status-icon" 
                  />
                  {status}
                </div>
              )}
            </form>
          </div>
          
          {/* Contact Information */}
          <div className={`contact-info-container ${animated ? 'animated' : ''}`}>
            <div className="info-header">
              <h2>Get in Touch</h2>
              <p>Have questions or need a quote? Contact us directly or use the form.</p>
            </div>
            
            <div className="info-content">
              <div className="contact-info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={faMobile} />
                </div>
                <div className="info-details">
                  <h3>Phone Numbers</h3>
                  <p><a href="tel:07735005695">07735 005 695</a></p>
                  <p><a href="tel:07393861306">07393 861 306</a></p>
                </div>
              </div>
              
              <div className="contact-info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <div className="info-details">
                  <h3>Email Address</h3>
                  <p><a href="mailto:info@oldglossooproofing.co.uk">oldglossoproofing@gmail.com</a></p>
                </div>
              </div>
              
              <div className="contact-info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </div>
                <div className="info-details">
                  <h3>Service Areas</h3>
                  <p>Glossop, Hadfield, Manchester, Stalybridge, Stockport and surrounding areas</p>
                </div>
              </div>
              
            </div>
            
            <div className="emergency-callout">
              <div className="emergency-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div className="emergency-content">
                <h3>Emergency Roof Repairs</h3>
                <p>Emergency Roof Repairs</p>
                <a href="tel:07735005695" className="emergency-button">Call Now</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Google Map */}
      <div className="map-section">
        <div className="container">
          <div className="map-wrapper">
            <div className="map-header">
              <h2>Our Service Area</h2>
              <p>Based in Glossop, we service the surrounding areas</p>
            </div>
            <div className="map-container">
              <iframe
                title="Service Area Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d19046.153818672876!2d-1.9701268288696283!3d53.44262627679333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487bba2e732c35db%3A0x44c68783568fece0!2sGlossop!5e0!3m2!1sen!2suk!4v1690540545735!5m2!1sen!2suk"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;